import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { clientesAPI } from '../services/clientesAPI';
import {
  Plus,
  MapPin,
  User,
  CheckCircle,
  Trash2,
  Settings,
  Coffee,
  Building2,
} from 'lucide-react';
import { useGooglePlaces } from '../hooks/useGooglePlaces';
import { useRecetasPorTipoPlato } from '../hooks/useRecetas';
import {
  parseCategoriasAccesoV2,
  type CategoriasAccesoV2,
} from '@suminuta/shared';

const TIPO_PLATO_BEBIDA_ID = 11;
const BLOQUES_ORDEN = ['desayuno', 'almuerzo', 'cena'] as const;
const BLOQUE_TITULOS: Record<string, string> = {
  desayuno: 'Desayuno',
  almuerzo: 'Almuerzo',
  cena: 'Cena',
};
type Bloque = (typeof BLOQUES_ORDEN)[number];

// ============================================================================
// INTERFACES
// ============================================================================

interface DireccionUI {
  id?: number;
  nombre_direccion: string;
  direccion: string;
  comuna: string;
  ciudad: string;
  codigo_postal: string;
  referencia: string;
  contacto_nombre: string;
  contacto_telefono: string;
}

interface OperacionUI {
  tempId: string; // React key (estable durante el flujo del wizard)
  id?: number; // presente => UPDATE, ausente => INSERT en backend
  nombre: string;
  es_principal: boolean;
  direccion: DireccionUI;
  tiposSeleccionados: number[];
  opcionesPorTipo: Record<string, number[]>;
  bebidasSeleccionadas: number[];
}

interface ClienteOperacionEntrada {
  id?: number;
  nombre: string;
  es_principal: boolean;
  activa?: boolean;
  direccion?: Record<string, unknown> | null;
  // El backend devuelve el shape v2 ya parseado (FASE B), pero el parser
  // local lo normaliza igual si llega legacy o string. Tipado unknown
  // para tolerar ambos sin perder seguridad en el caller.
  categorias_acceso: unknown;
}

interface Cliente {
  id: number;
  nombre: string;
  rut: string;
  email: string;
  nombre_contacto: string;
  telefono_contacto?: string;
  tipo_cliente: 'persona_natural' | 'juridica';
  razon_social?: string;
  representante_legal?: string;
  observaciones_despacho?: string;
  categorias_acceso: unknown;
  usuario_id?: number;
  activo: boolean;
  created_at: Date;
  updated_at: Date;
}

interface ClienteDireccion {
  id: number;
  cliente_id: number;
  nombre_direccion: string;
  direccion: string;
  comuna?: string;
  ciudad?: string;
  codigo_postal?: string;
  referencia?: string;
  contacto_nombre?: string;
  contacto_telefono?: string;
  es_principal: boolean;
  activa: boolean;
}

interface CategoriaReceta {
  id: number;
  nombre: string;
  descripcion: string | null;
  orden?: number | null;
  color?: string | null;
  activo?: boolean | null;
  bloque?: string | null;
  filas_default?: number | null;
}

interface ClienteCompleto extends Cliente {
  direcciones: ClienteDireccion[];
  categorias: CategoriaReceta[];
  operaciones?: ClienteOperacionEntrada[];
  usuario?: { id: number; nombre: string; email: string };
}

interface ClienteWizardProps {
  cliente: ClienteCompleto | null;
  onGuardar: (datos: any) => void;
  onCancelar: () => void;
  categoriasDisponibles: CategoriaReceta[];
}

// ============================================================================
// HELPERS PURE
// ============================================================================

let tempIdCounter = 0;
const nextTempId = (): string => `op-${++tempIdCounter}-${Math.random().toString(36).slice(2, 8)}`;

const direccionVacia = (): DireccionUI => ({
  nombre_direccion: '',
  direccion: '',
  comuna: '',
  ciudad: '',
  codigo_postal: '',
  referencia: '',
  contacto_nombre: '',
  contacto_telefono: '',
});

const operacionVacia = (esPrincipal: boolean): OperacionUI => ({
  tempId: nextTempId(),
  nombre: esPrincipal ? 'Casa Matriz' : '',
  es_principal: esPrincipal,
  direccion: direccionVacia(),
  tiposSeleccionados: [1],
  opcionesPorTipo: {},
  bebidasSeleccionadas: [],
});

const derivarOperacionesIniciales = (
  cliente: ClienteCompleto | null
): OperacionUI[] => {
  // Caso 1: backend nos pasó cliente.operaciones (GET /api/clientes/:id
  // enriquecido en FASE B). Hidratar tal cual.
  if (cliente?.operaciones && cliente.operaciones.length > 0) {
    return cliente.operaciones.map((op) => {
      const contrato = parseCategoriasAccesoV2(op.categorias_acceso);
      const dir = (op.direccion ?? null) as Record<string, unknown> | null;
      const dirStr = (k: string): string => {
        const v = dir?.[k];
        return typeof v === 'string' ? v : '';
      };
      const dirId = typeof dir?.id === 'number' ? (dir.id as number) : undefined;
      return {
        tempId: nextTempId(),
        id: op.id,
        nombre: op.nombre || 'Casa Matriz',
        es_principal: Boolean(op.es_principal),
        direccion: {
          id: dirId,
          nombre_direccion: dirStr('nombre_direccion'),
          direccion: dirStr('direccion'),
          comuna: dirStr('comuna'),
          ciudad: dirStr('ciudad'),
          codigo_postal: dirStr('codigo_postal'),
          referencia: dirStr('referencia'),
          contacto_nombre: dirStr('contacto_nombre'),
          contacto_telefono: dirStr('contacto_telefono'),
        },
        tiposSeleccionados: contrato?.tipos ?? [1],
        opcionesPorTipo: contrato?.opciones ?? {},
        bebidasSeleccionadas: contrato?.bebidas ?? [],
      };
    });
  }

  // Caso 2: legacy (cliente sin operaciones, solo direcciones + contrato
  // a nivel cliente). Crear UNA operación derivada.
  if (cliente) {
    const principalDir =
      cliente.direcciones?.find((d) => d.es_principal) ||
      cliente.direcciones?.[0] ||
      null;
    const contrato = parseCategoriasAccesoV2(cliente.categorias_acceso);
    return [
      {
        tempId: nextTempId(),
        nombre: 'Casa Matriz',
        es_principal: true,
        direccion: principalDir
          ? {
              id: principalDir.id,
              nombre_direccion: principalDir.nombre_direccion,
              direccion: principalDir.direccion,
              comuna: principalDir.comuna ?? '',
              ciudad: principalDir.ciudad ?? '',
              codigo_postal: principalDir.codigo_postal ?? '',
              referencia: principalDir.referencia ?? '',
              contacto_nombre: principalDir.contacto_nombre ?? '',
              contacto_telefono: principalDir.contacto_telefono ?? '',
            }
          : direccionVacia(),
        tiposSeleccionados:
          contrato?.tipos ?? cliente.categorias?.map((c) => c.id) ?? [1],
        opcionesPorTipo: contrato?.opciones ?? {},
        bebidasSeleccionadas: contrato?.bebidas ?? [],
      },
    ];
  }

  // Caso 3: cliente nuevo, una operación default.
  return [operacionVacia(true)];
};

const operacionAPayload = (op: OperacionUI) => {
  const opcionesFiltradas: Record<string, number[]> = {};
  Object.entries(op.opcionesPorTipo).forEach(([tipoIdStr, filas]) => {
    if (
      op.tiposSeleccionados.includes(parseInt(tipoIdStr, 10)) &&
      filas.length > 0
    ) {
      opcionesFiltradas[tipoIdStr] = filas;
    }
  });
  const contratoV2: CategoriasAccesoV2 = { tipos: op.tiposSeleccionados };
  if (Object.keys(opcionesFiltradas).length > 0) {
    contratoV2.opciones = opcionesFiltradas;
  }
  if (op.tiposSeleccionados.includes(TIPO_PLATO_BEBIDA_ID)) {
    contratoV2.bebidas = op.bebidasSeleccionadas;
  }
  const direccionPayload = op.direccion.direccion.trim()
    ? {
        ...(op.direccion.id !== undefined ? { id: op.direccion.id } : {}),
        nombre_direccion: op.direccion.nombre_direccion || op.nombre,
        direccion: op.direccion.direccion,
        ...(op.direccion.comuna ? { comuna: op.direccion.comuna } : {}),
        ...(op.direccion.ciudad ? { ciudad: op.direccion.ciudad } : {}),
        ...(op.direccion.codigo_postal
          ? { codigo_postal: op.direccion.codigo_postal }
          : {}),
        ...(op.direccion.referencia
          ? { referencia: op.direccion.referencia }
          : {}),
        ...(op.direccion.contacto_nombre
          ? { contacto_nombre: op.direccion.contacto_nombre }
          : {}),
        ...(op.direccion.contacto_telefono
          ? { contacto_telefono: op.direccion.contacto_telefono }
          : {}),
      }
    : undefined;

  return {
    ...(op.id !== undefined ? { id: op.id } : {}),
    nombre: op.nombre,
    es_principal: op.es_principal,
    ...(direccionPayload ? { direccion: direccionPayload } : {}),
    categorias_acceso: contratoV2,
  };
};

// ============================================================================
// COMPONENTE
// ============================================================================

const ClienteWizard: React.FC<ClienteWizardProps> = ({
  cliente,
  onGuardar,
  onCancelar,
  categoriasDisponibles,
}) => {
  // Validadores chilenos
  const validarRutChileno = (rut: string): boolean => {
    const rutLimpio = rut.replace(/[.\-\s]/g, '').toUpperCase();
    if (!/^[0-9]{7,8}[0-9K]$/.test(rutLimpio)) return false;
    const numero = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1);
    let suma = 0;
    let mult = 2;
    for (let i = numero.length - 1; i >= 0; i--) {
      suma += parseInt(numero[i]) * mult;
      mult = mult === 7 ? 2 : mult + 1;
    }
    const resto = suma % 11;
    const dvCalc =
      resto === 0 ? '0' : resto === 1 ? 'K' : (11 - resto).toString();
    return dv === dvCalc;
  };

  const validarTelefonoChileno = (tel: string): boolean => {
    const limpio = tel.replace(/[\s()\-]/g, '');
    return [
      /^\+56[2-9][0-9]{8}$/,
      /^[2-9][0-9]{8}$/,
      /^\+569[0-9]{8}$/,
      /^9[0-9]{8}$/,
    ].some((p) => p.test(limpio));
  };

  const validarEmail = (email: string): boolean =>
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
      email
    );

  const formatearRut = (rut: string): string => {
    const limpio = rut.replace(/[.\-\s]/g, '').toUpperCase();
    if (limpio.length < 2) return limpio;
    const numero = limpio.slice(0, -1);
    const dv = limpio.slice(-1);
    return `${numero.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`;
  };

  const formatearTelefono = (tel: string): string => {
    const limpio = tel.replace(/[\s()\-]/g, '');
    if (limpio.startsWith('+56')) {
      const num = limpio.slice(3);
      if (num.length === 9 && num.startsWith('9')) {
        return `+56 ${num.slice(0, 1)} ${num.slice(1, 5)} ${num.slice(5)}`;
      }
      if (num.length === 9) {
        return `+56 ${num.slice(0, 2)} ${num.slice(2, 6)} ${num.slice(6)}`;
      }
    }
    if (limpio.length === 9) {
      if (limpio.startsWith('9')) {
        return `${limpio.slice(0, 1)} ${limpio.slice(1, 5)} ${limpio.slice(5)}`;
      }
      return `${limpio.slice(0, 2)} ${limpio.slice(2, 5)} ${limpio.slice(5)}`;
    }
    return tel;
  };

  // === State ===
  const [pasoActual, setPasoActual] = useState(1);
  const totalPasos = 3;

  const [formData, setFormData] = useState({
    nombre: cliente?.nombre || '',
    rut: cliente?.rut || '',
    email: cliente?.email || '',
    nombre_contacto: cliente?.nombre_contacto || '',
    telefono_contacto: cliente?.telefono_contacto || '',
    tipo_cliente: cliente?.tipo_cliente || ('persona_natural' as const),
    razon_social: cliente?.razon_social || '',
    representante_legal: cliente?.representante_legal || '',
    observaciones_despacho: cliente?.observaciones_despacho || '',
    usuario_id: cliente?.usuario_id ?? null,
    activo: cliente?.activo ?? true,
  });

  const [operaciones, setOperaciones] = useState<OperacionUI[]>(() =>
    derivarOperacionesIniciales(cliente)
  );

  // Usuarios de rol cliente disponibles para vincular a esta ficha.
  //
  // El vínculo (`clientes.usuario_id`) es lo que permite que el cliente entre
  // al portal y vea SU ficha en /armar-pedido. Antes acá había una casilla
  // "Crear usuario de acceso" que el backend validaba y nunca usaba: quien la
  // marcaba creía que quedaba vinculado, y no. Por eso ahora se elige un
  // usuario concreto de una lista, en vez de prometer que se creará uno.
  const [usuariosVinculables, setUsuariosVinculables] = useState<
    Array<{ id: number; nombre: string; email: string }>
  >([]);
  const [cargandoUsuarios, setCargandoUsuarios] = useState(false);
  const [errorUsuarios, setErrorUsuarios] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setCargandoUsuarios(true);
    setErrorUsuarios(false);
    clientesAPI
      .usuariosVinculables(cliente?.id)
      .then((resp: { data?: Array<{ id: number; nombre: string; email: string }> }) => {
        if (!cancelado) setUsuariosVinculables(resp?.data ?? []);
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        console.error('Error cargando usuarios vinculables:', err);
        setErrorUsuarios(true);
      })
      .finally(() => {
        if (!cancelado) setCargandoUsuarios(false);
      });
    return () => {
      cancelado = true;
    };
  }, [cliente?.id]);

  const [errores, setErrores] = useState<Record<string, string>>({});

  // Google Places (compartido entre todas las cards via índice).
  const {
    searchPlaces,
    getPlaceDetails,
    predictions,
    isLoading: isPlacesLoading,
    isLoaded: isPlacesLoaded,
  } = useGooglePlaces();
  const [showSuggestions, setShowSuggestions] = useState<
    Record<string, boolean>
  >({});
  const [currentSearchTempId, setCurrentSearchTempId] = useState<string | null>(
    null
  );
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Catálogo BEBIDA: lo fetchea siempre y lo compartimos entre cards
  // (cualquier card con tipo 11 lo necesita). Caché de React Query lo dedupea.
  const algunaBebidaActiva = operaciones.some((op) =>
    op.tiposSeleccionados.includes(TIPO_PLATO_BEBIDA_ID)
  );
  const { data: bebidasDisponibles, isLoading: cargandoBebidas } =
    useRecetasPorTipoPlato(TIPO_PLATO_BEBIDA_ID, {
      enabled: algunaBebidaActiva,
    });

  // Agrupar tipos por bloque (sin BEBIDA, que va aparte)
  const categoriasPorBloque: Record<Bloque, CategoriaReceta[]> = useMemo(() => {
    const grupos: Record<Bloque, CategoriaReceta[]> = {
      desayuno: [],
      almuerzo: [],
      cena: [],
    };
    categoriasDisponibles.forEach((cat) => {
      if (cat.id === TIPO_PLATO_BEBIDA_ID) return;
      const bloque: Bloque =
        cat.bloque === 'desayuno' ||
        cat.bloque === 'cena' ||
        cat.bloque === 'almuerzo'
          ? cat.bloque
          : 'almuerzo';
      grupos[bloque].push(cat);
    });
    BLOQUES_ORDEN.forEach((b) =>
      grupos[b].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
    );
    return grupos;
  }, [categoriasDisponibles]);

  const categoriaBebida = categoriasDisponibles.find(
    (cat) => cat.id === TIPO_PLATO_BEBIDA_ID
  );

  // === Validaciones ===
  const validarPaso1 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.nombre.trim()) errs.nombre = 'El nombre es requerido';
    else if (formData.nombre.trim().length < 2)
      errs.nombre = 'Mínimo 2 caracteres';
    if (!formData.rut.trim()) errs.rut = 'El RUT es requerido';
    else if (!validarRutChileno(formData.rut))
      errs.rut = 'RUT inválido. Formato: 12.345.678-9';
    if (!formData.email.trim()) errs.email = 'El email es requerido';
    else if (!validarEmail(formData.email)) errs.email = 'Email inválido';
    if (!formData.nombre_contacto.trim())
      errs.nombre_contacto = 'El contacto es requerido';
    if (formData.telefono_contacto && formData.telefono_contacto.trim()) {
      if (!validarTelefonoChileno(formData.telefono_contacto)) {
        errs.telefono_contacto = 'Teléfono inválido';
      }
    }
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  const validarPaso2 = (): boolean => {
    const errs: Record<string, string> = {};
    if (operaciones.length === 0) {
      errs.operaciones = 'Debe definir al menos una operación';
      setErrores(errs);
      return false;
    }
    const principales = operaciones.filter((o) => o.es_principal);
    if (principales.length !== 1) {
      errs.operaciones = 'Debe haber exactamente UNA operación principal';
    }
    operaciones.forEach((op) => {
      if (!op.nombre.trim()) {
        errs[`op_nombre_${op.tempId}`] = 'El nombre de la operación es requerido';
      }
      if (op.tiposSeleccionados.length === 0) {
        errs[`op_tipos_${op.tempId}`] =
          'Debe seleccionar al menos una categoría de acceso';
      }
      if (
        op.tiposSeleccionados.includes(TIPO_PLATO_BEBIDA_ID) &&
        op.bebidasSeleccionadas.length === 0
      ) {
        errs[`op_bebidas_${op.tempId}`] =
          'Si BEBIDA está marcada, debe seleccionar al menos una bebida';
      }
      if (
        op.direccion.contacto_telefono &&
        op.direccion.contacto_telefono.trim()
      ) {
        if (!validarTelefonoChileno(op.direccion.contacto_telefono)) {
          errs[`op_telcontacto_${op.tempId}`] =
            'Teléfono de contacto de despacho inválido';
        }
      }
      // tip: nombre_direccion y dirección son opcionales en MVP — sin
      // dirección la operación queda "sin despacho asignado" (Manuel la
      // completa después). Esto evita bloquear al crear un cliente nuevo
      // sin saber aún las direcciones.
    });
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  // === Navegación ===
  const siguientePaso = () => {
    const ok = pasoActual === 1 ? validarPaso1() : pasoActual === 2 ? validarPaso2() : true;
    if (ok && pasoActual < totalPasos) setPasoActual(pasoActual + 1);
  };
  const pasoAnterior = () => {
    if (pasoActual > 1) {
      setPasoActual(pasoActual - 1);
      setErrores({});
    }
  };
  const irAPaso = (p: number) => {
    setPasoActual(p);
    setErrores({});
  };

  // === Handlers cliente data ===
  const handleInputChange = (field: string, value: unknown) => {
    let v = value;
    if (field === 'rut' && typeof value === 'string') v = formatearRut(value);
    else if (field === 'telefono_contacto' && typeof value === 'string')
      v = formatearTelefono(value);
    setFormData((prev) => ({ ...prev, [field]: v }));
    if (errores[field]) setErrores((prev) => ({ ...prev, [field]: '' }));
  };

  // === Handlers operaciones ===
  const handleAgregarOperacion = () => {
    setOperaciones((prev) => [...prev, operacionVacia(false)]);
  };
  const handleEliminarOperacion = (tempId: string) => {
    setOperaciones((prev) => {
      if (prev.length <= 1) return prev; // mínimo 1
      const filt = prev.filter((o) => o.tempId !== tempId);
      // Si quitamos la principal, marcar la primera como principal.
      if (!filt.some((o) => o.es_principal)) {
        filt[0] = { ...filt[0], es_principal: true };
      }
      return filt;
    });
  };
  const handleMarcarPrincipal = (tempId: string) => {
    setOperaciones((prev) =>
      prev.map((o) => ({ ...o, es_principal: o.tempId === tempId }))
    );
  };
  const updateOperacion = (
    tempId: string,
    updater: (op: OperacionUI) => OperacionUI
  ) => {
    setOperaciones((prev) =>
      prev.map((o) => (o.tempId === tempId ? updater(o) : o))
    );
  };

  const handleNombreOpChange = (tempId: string, nombre: string) => {
    updateOperacion(tempId, (op) => ({ ...op, nombre }));
    if (errores[`op_nombre_${tempId}`]) {
      setErrores((prev) => ({ ...prev, [`op_nombre_${tempId}`]: '' }));
    }
  };

  const handleDireccionChange = (
    tempId: string,
    field: keyof DireccionUI,
    value: string
  ) => {
    const formatted =
      field === 'contacto_telefono' && typeof value === 'string'
        ? formatearTelefono(value)
        : value;
    updateOperacion(tempId, (op) => ({
      ...op,
      direccion: { ...op.direccion, [field]: formatted },
    }));
  };

  // Google Places por card (tempId)
  const handleAddressSearch = (tempId: string, query: string) => {
    if (query.length < 3) {
      setShowSuggestions((prev) => ({ ...prev, [tempId]: false }));
      return;
    }
    setCurrentSearchTempId(tempId);
    searchPlaces(query);
    setShowSuggestions((prev) => ({ ...prev, [tempId]: true }));
  };
  const handleAddressInputChange = (tempId: string, value: string) => {
    handleDireccionChange(tempId, 'direccion', value);
    if (debounceRef.current[tempId]) clearTimeout(debounceRef.current[tempId]);
    debounceRef.current[tempId] = setTimeout(
      () => handleAddressSearch(tempId, value),
      500
    );
  };
  const handleSelectAddress = async (
    tempId: string,
    place: { place_id: string; description: string }
  ) => {
    try {
      const details = await getPlaceDetails(place.place_id);
      handleDireccionChange(tempId, 'direccion', place.description);
      if (details?.comuna) handleDireccionChange(tempId, 'comuna', details.comuna);
      if (details?.ciudad) handleDireccionChange(tempId, 'ciudad', details.ciudad);
      if (details?.codigo_postal)
        handleDireccionChange(tempId, 'codigo_postal', details.codigo_postal);
      setShowSuggestions((prev) => ({ ...prev, [tempId]: false }));
      setCurrentSearchTempId(null);
    } catch (e) {
      console.error('Error obteniendo detalles del lugar:', e);
    }
  };

  // Contrato handlers por operación
  const handleToggleTipo = (tempId: string, tipoId: number) => {
    updateOperacion(tempId, (op) => {
      if (op.tiposSeleccionados.includes(tipoId)) {
        const sigue = op.tiposSeleccionados.filter((id) => id !== tipoId);
        const { [String(tipoId)]: _drop, ...restoOpc } = op.opcionesPorTipo;
        return {
          ...op,
          tiposSeleccionados: sigue,
          opcionesPorTipo: restoOpc,
          bebidasSeleccionadas:
            tipoId === TIPO_PLATO_BEBIDA_ID ? [] : op.bebidasSeleccionadas,
        };
      }
      return { ...op, tiposSeleccionados: [...op.tiposSeleccionados, tipoId] };
    });
  };
  const handleToggleOpcion = (tempId: string, tipoId: number, fila: number) => {
    updateOperacion(tempId, (op) => {
      const actuales = op.opcionesPorTipo[String(tipoId)] ?? [];
      const yaIncluida = actuales.includes(fila);
      const nuevas = yaIncluida
        ? actuales.filter((f) => f !== fila)
        : [...actuales, fila].sort((a, b) => a - b);
      const nextOpc = { ...op.opcionesPorTipo };
      if (nuevas.length === 0) delete nextOpc[String(tipoId)];
      else nextOpc[String(tipoId)] = nuevas;
      return { ...op, opcionesPorTipo: nextOpc };
    });
  };
  const handleToggleBebida = (tempId: string, recetaId: number) => {
    updateOperacion(tempId, (op) => ({
      ...op,
      bebidasSeleccionadas: op.bebidasSeleccionadas.includes(recetaId)
        ? op.bebidasSeleccionadas.filter((id) => id !== recetaId)
        : [...op.bebidasSeleccionadas, recetaId],
    }));
  };

  // === Submit ===
  const handleSubmit = () => {
    if (!validarPaso1() || !validarPaso2()) return;

    const operacionesPayload = operaciones.map(operacionAPayload);
    const principal = operacionesPayload.find((op) => op.es_principal);

    const datosParaGuardar = {
      ...formData,
      rut: formData.rut.replace(/[.\-\s]/g, ''),
      operaciones: operacionesPayload,
      // Espejo opcional al nivel cliente del contrato principal — el backend
      // también lo hace, esto solo facilita compat para cualquier consumidor
      // del payload (ej. logs).
      categorias_acceso: principal?.categorias_acceso ?? null,
      categorias: categoriasDisponibles.filter((cat) =>
        principal?.categorias_acceso.tipos.includes(cat.id)
      ),
    };
    onGuardar(datosParaGuardar);
  };

  const getTituloPaso = (): string => {
    switch (pasoActual) {
      case 1:
        return 'Datos del Cliente';
      case 2:
        return 'Operaciones';
      case 3:
        return 'Revisión y Confirmación';
      default:
        return 'Paso';
    }
  };

  // ============================================================================
  // RENDERS
  // ============================================================================

  const renderIndicadorProgreso = () => (
    <div className="flex items-center justify-center mb-8">
      {Array.from({ length: totalPasos }, (_, idx) => {
        const paso = idx + 1;
        const activo = paso === pasoActual;
        const completado = paso < pasoActual;
        return (
          <React.Fragment key={paso}>
            <motion.div
              className={`flex items-center justify-center w-10 h-10 rounded-full border-2 cursor-pointer ${
                completado
                  ? 'bg-green-500 border-green-500 text-white'
                  : activo
                    ? 'bg-blue-500 border-blue-500 text-white'
                    : 'bg-gray-200 border-gray-300 text-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400'
              }`}
              onClick={() => irAPaso(paso)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {completado ? <CheckCircle className="w-5 h-5" /> : paso}
            </motion.div>
            {paso < totalPasos && (
              <div
                className={`w-12 h-0.5 mx-2 ${
                  paso < pasoActual ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // ---------- Paso 1: Datos del Cliente ----------
  const renderPaso1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Tipo de cliente
          </label>
          <select
            value={formData.tipo_cliente}
            onChange={(e) => handleInputChange('tipo_cliente', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
          >
            <option value="persona_natural">Particular</option>
            <option value="juridica">Empresa</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Nombre / Razón social*
          </label>
          <input
            type="text"
            value={formData.nombre}
            onChange={(e) => handleInputChange('nombre', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
            placeholder="Ej. EMPRESA EJEMPLO S.A."
          />
          {errores.nombre && (
            <p className="text-red-500 text-xs mt-1">{errores.nombre}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            RUT*
          </label>
          <input
            type="text"
            value={formData.rut}
            onChange={(e) => handleInputChange('rut', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
            placeholder="12.345.678-9"
          />
          {errores.rut && (
            <p className="text-red-500 text-xs mt-1">{errores.rut}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Email*
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
            placeholder="contacto@empresa.cl"
          />
          {errores.email && (
            <p className="text-red-500 text-xs mt-1">{errores.email}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Nombre del contacto*
          </label>
          <input
            type="text"
            value={formData.nombre_contacto}
            onChange={(e) =>
              handleInputChange('nombre_contacto', e.target.value)
            }
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
          />
          {errores.nombre_contacto && (
            <p className="text-red-500 text-xs mt-1">
              {errores.nombre_contacto}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Teléfono del contacto
          </label>
          <input
            type="tel"
            value={formData.telefono_contacto}
            onChange={(e) =>
              handleInputChange('telefono_contacto', e.target.value)
            }
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
            placeholder="+56 9 1234 5678"
          />
          {errores.telefono_contacto && (
            <p className="text-red-500 text-xs mt-1">
              {errores.telefono_contacto}
            </p>
          )}
        </div>
        {formData.tipo_cliente === 'juridica' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Razón social
              </label>
              <input
                type="text"
                value={formData.razon_social}
                onChange={(e) =>
                  handleInputChange('razon_social', e.target.value)
                }
                className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Representante legal
              </label>
              <input
                type="text"
                value={formData.representante_legal}
                onChange={(e) =>
                  handleInputChange('representante_legal', e.target.value)
                }
                className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
              />
            </div>
          </>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Observaciones de despacho
        </label>
        <textarea
          value={formData.observaciones_despacho}
          onChange={(e) =>
            handleInputChange('observaciones_despacho', e.target.value)
          }
          rows={2}
          className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
          placeholder="Acceso a portería, horarios, etc."
        />
      </div>
      <div className="flex items-center gap-6 pt-2">
        <label className="inline-flex items-center text-sm">
          <input
            type="checkbox"
            checked={formData.activo}
            onChange={(e) => handleInputChange('activo', e.target.checked)}
            className="mr-2"
          />
          Cliente activo
        </label>
      </div>
    </div>
  );

  // ---------- Paso 2: Operaciones (cards) ----------
  const renderContratoOp = (op: OperacionUI) => (
    <div className="space-y-4">
      <div>
        <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center">
          <Settings className="h-4 w-4 mr-1" />
          Contrato de acceso
        </h5>
        {BLOQUES_ORDEN.map((bloque) => {
          const items = categoriasPorBloque[bloque];
          if (items.length === 0) return null;
          return (
            <div key={bloque} className="mb-3">
              <h6 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase mb-1">
                {BLOQUE_TITULOS[bloque]}
              </h6>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((cat) => {
                  const marcado = op.tiposSeleccionados.includes(cat.id);
                  const filasDef = cat.filas_default ?? 1;
                  const opActuales =
                    op.opcionesPorTipo[String(cat.id)] ?? [];
                  return (
                    <div
                      key={cat.id}
                      className={`border rounded p-2 ${
                        marcado
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={() => handleToggleTipo(op.tempId, cat.id)}
                          className="h-4 w-4 mt-0.5 mr-2"
                        />
                        <span className="text-sm">{cat.nombre}</span>
                      </label>
                      {marcado && filasDef >= 2 && (
                        <div className="ml-6 mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-gray-500 mb-1">
                            Opciones (vacío = todas):
                          </p>
                          <div className="flex gap-3 flex-wrap">
                            {Array.from(
                              { length: filasDef },
                              (_, i) => i + 1
                            ).map((fila) => (
                              <label
                                key={fila}
                                className="inline-flex items-center text-xs cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={opActuales.includes(fila)}
                                  onChange={() =>
                                    handleToggleOpcion(op.tempId, cat.id, fila)
                                  }
                                  className="h-3 w-3 mr-1"
                                />
                                Op. {fila}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {categoriaBebida && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <h6 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase mb-2 flex items-center">
              <Coffee className="h-3 w-3 mr-1" /> Bebidas
            </h6>
            <div
              className={`border rounded p-2 ${
                op.tiposSeleccionados.includes(TIPO_PLATO_BEBIDA_ID)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={op.tiposSeleccionados.includes(TIPO_PLATO_BEBIDA_ID)}
                  onChange={() =>
                    handleToggleTipo(op.tempId, TIPO_PLATO_BEBIDA_ID)
                  }
                  className="h-4 w-4 mt-0.5 mr-2"
                />
                <span className="text-sm">Habilitar BEBIDA</span>
              </label>
              {op.tiposSeleccionados.includes(TIPO_PLATO_BEBIDA_ID) && (
                <div className="mt-2 pl-6">
                  {cargandoBebidas ? (
                    <p className="text-xs text-gray-500">Cargando…</p>
                  ) : !bebidasDisponibles || bebidasDisponibles.length === 0 ? (
                    <p className="text-xs text-amber-600 dark:text-amber-300">
                      Sin recetas BEBIDA activas en el catálogo.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {bebidasDisponibles.map((b) => (
                        <label
                          key={b.id}
                          className="inline-flex items-center text-xs cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={op.bebidasSeleccionadas.includes(b.id)}
                            onChange={() => handleToggleBebida(op.tempId, b.id)}
                            className="h-3 w-3 mr-1"
                          />
                          {b.nombre}
                        </label>
                      ))}
                    </div>
                  )}
                  {errores[`op_bebidas_${op.tempId}`] && (
                    <p className="text-red-500 text-xs mt-1">
                      {errores[`op_bebidas_${op.tempId}`]}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        {errores[`op_tipos_${op.tempId}`] && (
          <p className="text-red-500 text-xs mt-2">
            {errores[`op_tipos_${op.tempId}`]}
          </p>
        )}
      </div>
    </div>
  );

  const renderDireccionOp = (op: OperacionUI) => (
    <div className="space-y-3">
      <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2 flex items-center">
        <MapPin className="h-4 w-4 mr-1" />
        Dirección de despacho
      </h5>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Etiqueta de la dirección
          </label>
          <input
            type="text"
            value={op.direccion.nombre_direccion}
            onChange={(e) =>
              handleDireccionChange(
                op.tempId,
                'nombre_direccion',
                e.target.value
              )
            }
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white text-sm"
            placeholder={`Ej. ${op.nombre || 'Faena Norte'}`}
          />
        </div>
        <div className="relative">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Dirección
            {isPlacesLoaded && (
              <span className="text-xs text-gray-400 ml-1">(autocompletado)</span>
            )}
          </label>
          <input
            type="text"
            value={op.direccion.direccion}
            onChange={(e) =>
              handleAddressInputChange(op.tempId, e.target.value)
            }
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white text-sm"
            placeholder="Calle 123, comuna"
          />
          {showSuggestions[op.tempId] &&
            currentSearchTempId === op.tempId &&
            predictions.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 max-h-48 overflow-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow">
                {predictions.map((p) => (
                  <button
                    type="button"
                    key={p.place_id}
                    onClick={() => handleSelectAddress(op.tempId, p)}
                    className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {p.description}
                  </button>
                ))}
              </div>
            )}
          {isPlacesLoading && (
            <p className="text-xs text-gray-400 mt-1">Buscando…</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Comuna
          </label>
          <input
            type="text"
            value={op.direccion.comuna}
            onChange={(e) =>
              handleDireccionChange(op.tempId, 'comuna', e.target.value)
            }
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Ciudad
          </label>
          <input
            type="text"
            value={op.direccion.ciudad}
            onChange={(e) =>
              handleDireccionChange(op.tempId, 'ciudad', e.target.value)
            }
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Contacto en el sitio
          </label>
          <input
            type="text"
            value={op.direccion.contacto_nombre}
            onChange={(e) =>
              handleDireccionChange(
                op.tempId,
                'contacto_nombre',
                e.target.value
              )
            }
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Teléfono del contacto
          </label>
          <input
            type="tel"
            value={op.direccion.contacto_telefono}
            onChange={(e) =>
              handleDireccionChange(
                op.tempId,
                'contacto_telefono',
                e.target.value
              )
            }
            className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white text-sm"
            placeholder="+56 9 1234 5678"
          />
          {errores[`op_telcontacto_${op.tempId}`] && (
            <p className="text-red-500 text-xs mt-1">
              {errores[`op_telcontacto_${op.tempId}`]}
            </p>
          )}
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
          Referencia / instrucciones
        </label>
        <textarea
          value={op.direccion.referencia}
          onChange={(e) =>
            handleDireccionChange(op.tempId, 'referencia', e.target.value)
          }
          rows={2}
          className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white text-sm"
          placeholder="Acceso, horarios, etc."
        />
      </div>
    </div>
  );

  const renderPaso2 = () => (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Cada cliente puede tener N operaciones (Casa Matriz, Faena Norte, …).
        Cada operación tiene su propia dirección y su propio contrato granular.
      </p>
      {errores.operaciones && (
        <p className="text-red-500 text-sm">{errores.operaciones}</p>
      )}
      {operaciones.map((op, idx) => (
        <div
          key={op.tempId}
          className="border border-gray-300 dark:border-gray-700 rounded-lg p-4"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center">
                <Building2 className="h-4 w-4 mr-1" /> Operación #{idx + 1}
              </label>
              <input
                type="text"
                value={op.nombre}
                onChange={(e) =>
                  handleNombreOpChange(op.tempId, e.target.value)
                }
                className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white"
                placeholder="Casa Matriz / Faena Norte / …"
              />
              {errores[`op_nombre_${op.tempId}`] && (
                <p className="text-red-500 text-xs mt-1">
                  {errores[`op_nombre_${op.tempId}`]}
                </p>
              )}
            </div>
            <label className="inline-flex items-center text-xs cursor-pointer mt-6">
              <input
                type="radio"
                name="op_principal"
                checked={op.es_principal}
                onChange={() => handleMarcarPrincipal(op.tempId)}
                className="mr-1"
              />
              Principal
            </label>
            {operaciones.length > 1 && (
              <button
                type="button"
                onClick={() => handleEliminarOperacion(op.tempId)}
                className="mt-6 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                title="Quitar operación"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          {renderDireccionOp(op)}
          <hr className="my-4 border-gray-200 dark:border-gray-700" />
          {renderContratoOp(op)}
        </div>
      ))}
      <button
        type="button"
        onClick={handleAgregarOperacion}
        className="inline-flex items-center px-3 py-2 border border-blue-500 text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded text-sm"
      >
        <Plus className="h-4 w-4 mr-1" /> Agregar operación
      </button>
    </div>
  );

  // ---------- Paso 3: Revisión ----------
  const renderPaso3 = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3 flex items-center">
          <User className="h-4 w-4 mr-2" /> Datos del cliente
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">Tipo: </span>
            <strong>
              {formData.tipo_cliente === 'persona_natural' ? 'Particular' : 'Empresa'}
            </strong>
          </div>
          <div>
            <span className="text-gray-500">Nombre: </span>
            <strong>{formData.nombre}</strong>
          </div>
          <div>
            <span className="text-gray-500">RUT: </span>
            <strong>{formData.rut}</strong>
          </div>
          <div>
            <span className="text-gray-500">Email: </span>
            <strong>{formData.email}</strong>
          </div>
          <div>
            <span className="text-gray-500">Contacto: </span>
            <strong>{formData.nombre_contacto}</strong>
          </div>
          {formData.telefono_contacto && (
            <div>
              <span className="text-gray-500">Tel: </span>
              <strong>{formData.telefono_contacto}</strong>
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3 flex items-center">
          <Building2 className="h-4 w-4 mr-2" /> Operaciones ({operaciones.length})
        </h4>
        <div className="space-y-3">
          {operaciones.map((op, idx) => {
            const tiposNombres = op.tiposSeleccionados
              .map((id) => categoriasDisponibles.find((c) => c.id === id)?.nombre)
              .filter(Boolean);
            const opcionesDetail = Object.entries(op.opcionesPorTipo)
              .map(([tId, filas]) => {
                const nombre = categoriasDisponibles.find(
                  (c) => c.id === parseInt(tId, 10)
                )?.nombre;
                return `${nombre} Op.${filas.join(',')}`;
              })
              .join(' · ');
            const bebidasNombres =
              op.tiposSeleccionados.includes(TIPO_PLATO_BEBIDA_ID) &&
              bebidasDisponibles
                ? bebidasDisponibles
                    .filter((b) => op.bebidasSeleccionadas.includes(b.id))
                    .map((b) => b.nombre)
                    .join(', ')
                : '';
            return (
              <div
                key={op.tempId}
                className="border border-gray-200 dark:border-gray-700 rounded p-3 text-sm"
              >
                <div className="flex items-center justify-between mb-1">
                  <strong>
                    #{idx + 1} {op.nombre}
                  </strong>
                  {op.es_principal && (
                    <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                      Principal
                    </span>
                  )}
                </div>
                {op.direccion.direccion ? (
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    {op.direccion.direccion}
                    {op.direccion.comuna && `, ${op.direccion.comuna}`}
                    {op.direccion.ciudad && `, ${op.direccion.ciudad}`}
                  </p>
                ) : (
                  <p className="text-xs text-amber-600 dark:text-amber-300">
                    Sin dirección asignada
                  </p>
                )}
                <p className="text-xs text-gray-700 dark:text-gray-200 mt-2">
                  <strong>Contrato:</strong> {tiposNombres.join(', ') || '—'}
                </p>
                {opcionesDetail && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Restricciones: {opcionesDetail}
                  </p>
                )}
                {bebidasNombres && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Bebidas: {bebidasNombres}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-1 flex items-center">
          <User className="h-4 w-4 mr-2" /> Usuario de acceso
        </h4>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          El usuario vinculado podrá entrar al portal y armar pedidos para este
          cliente. Sin vínculo, el cliente existe pero nadie puede pedir por él.
        </p>

        {errorUsuarios ? (
          <p className="text-sm text-red-600 dark:text-red-400">
            No se pudo cargar la lista de usuarios. Podés guardar el cliente
            igual y vincularlo después editándolo.
          </p>
        ) : (
          <>
            <select
              value={formData.usuario_id ?? ''}
              onChange={(e) =>
                handleInputChange(
                  'usuario_id',
                  e.target.value ? Number(e.target.value) : null
                )
              }
              disabled={cargandoUsuarios}
              className="w-full p-2 border border-gray-300 rounded dark:bg-gray-800 dark:text-white disabled:opacity-60"
            >
              <option value="">
                {cargandoUsuarios ? 'Cargando…' : 'Sin usuario vinculado'}
              </option>
              {usuariosVinculables.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre} — {u.email}
                </option>
              ))}
            </select>

            {!cargandoUsuarios && usuariosVinculables.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                No hay usuarios de rol cliente sin ficha asignada. Creá uno en
                Administración → Usuarios y volvé a editar este cliente.
              </p>
            )}
          </>
        )}
      </div>

      {!formData.activo && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded p-3 text-sm">
          <p>• El cliente quedará inactivo.</p>
        </div>
      )}
    </div>
  );

  // === Main render ===
  return (
    <div className="max-w-5xl mx-auto p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {cliente ? 'Editar cliente' : 'Nuevo cliente'} — {getTituloPaso()}
          </h2>
          <button
            type="button"
            onClick={onCancelar}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 text-sm"
          >
            Cancelar
          </button>
        </div>

        {renderIndicadorProgreso()}

        <div className="min-h-[400px]">
          {pasoActual === 1 && renderPaso1()}
          {pasoActual === 2 && renderPaso2()}
          {pasoActual === 3 && renderPaso3()}
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={pasoAnterior}
            disabled={pasoActual === 1}
            className="px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-40"
          >
            ← Anterior
          </button>
          {pasoActual < totalPasos ? (
            <button
              type="button"
              onClick={siguientePaso}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              Siguiente →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded inline-flex items-center"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              {cliente ? 'Guardar cambios' : 'Crear cliente'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClienteWizard;
